/* eslint-disable no-template-curly-in-string */
import yup from 'schemas/validation/feature'
import { anyPass, isEmpty, isNil } from 'ramda'
import { COMPLEX, TIMER, SIMPLE, SERVICE } from 'utils/constants'
import strings from 'utils/strings'

import featureMetaSchema from './featureMetaSchema'
import functionSchema from './functionSchema'
import querySchema from './querySchema'
import conditionSchema from './conditionSchema'

// determine if we test against a query or single condition
// single conditions are in the queries list (in our denormalized feature)
export const queryOrSingleCondition = yup.lazy((value) => {
  if (value && value.queryType === SIMPLE) {
    return conditionSchema
  }

  return querySchema
})

const checkForMappedOutputPin = {
  name: 'defined output pin',
  test() {
    const {
      path,
      originalValue: {
        outputMap: { outputs = [] },
      },
    } = this
    // check for mapped outputs but filter out any floating circuits
    const mappedOutputs = outputs
      .filter(({ floatingCircuit }) =>
        anyPass([isNil, isEmpty])(floatingCircuit)
      )
      .map(({ mappedOutputId }) => mappedOutputId)

    if (
      mappedOutputs.length !==
      mappedOutputs.filter((id) => id && id !== '').length
    )
      return this.createError({
        path,
        message: 'the output pin must be mapped',
      })

    return true
  },
}

const checkForDefinedOutputQuery = {
  name: 'one defined output query', // string not customer facing
  test: function test() {
    const { parent, path } = this

    const featureHasOneOutputQuery = parent.some(
      (fn) => !isNil(fn.outputMap.outputQuery)
    )

    if (featureHasOneOutputQuery) return true

    return this.createError({
      path,
      message: 'there must be at least one output query defined', // string not customer facing
    })
  },
}

const descriptionSchema = yup.string().unique()

/**
 * PBI-492348: Prevent duplicate native (SERVICE) output assignment within a
 * feature. Each native output signal may only be mapped to one output node.
 */
const checkForDuplicateNativeOutputs = {
  name: 'no-duplicate-native-outputs',
  test(functions) {
    if (!functions) return true
    const seen = new Set()
    for (const fn of functions) {
      const outputs = fn?.outputMap?.outputs || []
      for (const output of outputs) {
        if (output.type === SERVICE && output.mappedOutputId) {
          if (seen.has(output.mappedOutputId)) {
            return this.createError({
              message: strings.duplicate_native_output_assignment,
            })
          }
          seen.add(output.mappedOutputId)
        }
      }
    }
    return true
  },
}

export const featureSchema = featureMetaSchema.concat(
  yup.object({
    functions: yup
      .array()
      .of(
        functionSchema
          .test(checkForDefinedOutputQuery)
          .test(checkForMappedOutputPin)
      )
      .test(checkForDuplicateNativeOutputs),
    queries: yup.array().of(queryOrSingleCondition),
    revision: yup.string().nullable(),
    revisions: yup.array().of(
      yup.object({
        partNumber: yup.string(),
        uri: yup.string(),
        revision: yup.string(),
        status: yup.string(),
      })
    ),
  })
)

const arrayOfDescriptionsSchema = yup.array().of(
  yup.object({
    description: descriptionSchema,
  })
)

export const featureSaveSchema = yup.object({
  description: descriptionSchema,
  queries: yup.array().of(
    yup.object({
      queryType: yup.string(),
      description: yup
        .string()
        .nullable()
        .when('queryType', {
          is: (value) => value === COMPLEX || value === TIMER,
          then: descriptionSchema,
        }),
    })
  ),
  functions: yup.array().of(
    yup.object({
      description: descriptionSchema,
      outputMap: yup.object({
        outputs: arrayOfDescriptionsSchema,
      }),
    })
  ),
})
